import type {
  DiffsAnalyticsRequest,
  DiffsAnalyticsResponse,
  DiffsPagesRequest,
  DiffsPagesResponse,
  DiffsCrosstabRequest,
  DiffsCrosstabResponse,
} from '@/components/reports/graphs/types';

const DIFFS_ANALYTICS_URL = 'https://api.pricingsaas.com/functions/v1/api/analytics/diffs';
const DIFFS_PAGES_URL = 'https://api.pricingsaas.com/functions/v1/api/analytics/diffs/pages';
const DIFFS_CROSSTAB_URL = 'https://api.pricingsaas.com/functions/v1/api/analytics/diffs/crosstab';
const ANALYTICS_API_KEY = import.meta.env.VITE_ANALYTICS_API_KEY;

/**
 * Fetches diffs analytics data for event counts across multiple periods.
 * Returns breakdown by category, employees, or event_type based on request options.
 */
export async function fetchDiffsAnalytics(
  request: DiffsAnalyticsRequest
): Promise<DiffsAnalyticsResponse> {
  // Check if API key is configured
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  try {
    const response = await fetch(DIFFS_ANALYTICS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch diffs analytics';
      try {
        const errorData = await response.json();
        console.error('[diffsAnalyticsAPI] Error response:', errorData);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const data: DiffsAnalyticsResponse = await response.json();

    return data;
  } catch (error) {
    console.error('[diffsAnalyticsAPI] Fetch error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}

/**
 * Fetches pages/diffs matching a specific breakdown cell for drilldown.
 * Used when user clicks on a cell in the data table or chart.
 */
export async function fetchDiffsPages(
  request: DiffsPagesRequest
): Promise<DiffsPagesResponse> {
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  try {
    const response = await fetch(DIFFS_PAGES_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch diffs pages';
      try {
        const errorData = await response.json();
        console.error('[diffsAnalyticsAPI] Error response:', errorData);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const data: DiffsPagesResponse = await response.json();
    return data;
  } catch (error) {
    console.error('[diffsAnalyticsAPI] Fetch pages error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}

/**
 * Fetches diffs crosstab data grouping events by category or employees.
 * Returns cumulative event counts across all periods for each group.
 * Used for category-based stacked bar charts.
 */
export async function fetchDiffsCrosstab(
  request: DiffsCrosstabRequest
): Promise<DiffsCrosstabResponse> {
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  try {
    const response = await fetch(DIFFS_CROSSTAB_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch diffs crosstab';
      try {
        const errorData = await response.json();
        console.error('[diffsAnalyticsAPI] Crosstab error response:', errorData);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const data: DiffsCrosstabResponse = await response.json();
    return data;
  } catch (error) {
    console.error('[diffsAnalyticsAPI] Fetch crosstab error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}
